import React, { useCallback, useEffect, useRef } from 'react';
import { makeStyles, mergeClasses, shorthands } from '@fluentui/react-components';
import ChatInput from '../../ChatInput/components/ChatInput';
import ChatHistory from '../../ChatHistory/components/ChatHistory';
import ChatWindowScreen from '../../ChatWindow/components/ChatWindowScreen';
import MigrateButton from '../../ChatMigration/components/MigrateButton';
import MigrationContextBanner from '../../ChatMigration/components/MigrationContextBanner';
import { buildAvatarSrc } from '../../utils/AvatarUtils';
import { customFonts } from '../../../styles';
import { customColors } from '../../../styles';
import useChatRoom from '../hooks/useChatRoom';
import { Loading } from '../../../shared/ui/Loading';
import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';

const useStyles = makeStyles({
    root: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        ...shorthands.overflow('hidden'),
        fontFamily: customFonts.Lato,
    },
    rootEmptyState: {
        justifyContent: 'center',
        backgroundColor: customColors.protivitiOffWhite,
        gap: '18px',
    },
    scroll: {
        flex: 1,
        minHeight: 0,
        ...shorthands.padding('4px', '0'),
        ...shorthands.overflow('auto'),
    },
    scrollEmptyState: {
        flex: '0 0 auto',
        minHeight: 'auto',
        ...shorthands.padding('0'),
        overflow: 'visible',
    },
    input: {
        width: '100%',
    },
    inputEmptyState: {
        flex: '0 0 auto',
    },
    inputEmptyStateInner: {
        width: '100%',
        maxWidth: '980px',
        ...shorthands.margin('0', 'auto'),
        ...shorthands.padding('0', '24px', '24px', '24px'),
    },
    loadingState: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    loadingText: {
        fontFamily: customFonts.Lato,
    },
});

const SCROLL_THRESHOLD = 50;

const ChatRoom: React.FC = () => {
    const styles = useStyles();
    const vm = useChatRoom();
    const scrollRef = useRef<HTMLDivElement>(null);
    // stores scrollHeight captured just before a prepend so the view doesn't jump
    const prevScrollHeightRef = useRef<number | null>(null);

    useEffect(() => {
        if (!scrollRef.current) return;
        if (prevScrollHeightRef.current !== null) {
            // messages were prepended — restore the visual scroll position
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight - prevScrollHeightRef.current;
            prevScrollHeightRef.current = null;
        } else {
            // new message or initial load — scroll to bottom
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [vm.messages]);

    // When content doesn't overflow the container there is no scrollbar, so onScroll never fires.
    // Auto-load more pages until the container becomes scrollable or there are no more messages.
    useEffect(() => {
        if (!scrollRef.current || !vm.hasMoreMessages) return;
        if (scrollRef.current.scrollHeight <= scrollRef.current.clientHeight) {
            vm.loadMoreMessages();
        }
    }, [vm.messages, vm.hasMoreMessages, vm.loadMoreMessages]);

    const handleScroll = useCallback(() => {
        if (!scrollRef.current || !vm.hasMoreMessages) return;
        if (scrollRef.current.scrollTop <= SCROLL_THRESHOLD) {
            prevScrollHeightRef.current = scrollRef.current.scrollHeight;
            vm.loadMoreMessages();
        }
    }, [vm]);

    if (!vm.hasSession) {
        return <ChatWindowScreen />;
    }

    if (vm.isLoadingMessages) {
        return <Loading text="Loading messages..." />;
    }

    const isAssistants = vm.agentProviderType === ChatProviderType.Assistants;
    const rootClassName = vm.messagesEmpty ? mergeClasses(styles.root, styles.rootEmptyState) : styles.root;
    const scrollClassName = vm.messagesEmpty ? mergeClasses(styles.scroll, styles.scrollEmptyState) : styles.scroll;

    return (
        <div className={rootClassName}>
            {vm.migrationInfo && (
                <MigrationContextBanner
                    migratedFromTitle={vm.migrationInfo.migratedFromTitle}
                    migrationSummary={vm.migrationInfo.migrationSummary}
                    onDismiss={vm.onDismissMigrationBanner}
                />
            )}
            <div ref={scrollRef} className={scrollClassName} onScroll={handleScroll}>
                {vm.messagesEmpty ? (
                    <ChatWindowScreen
                        agentAvatarSrc={
                            vm.agentProviderType !== undefined
                                ? buildAvatarSrc(vm.agentProviderType, vm.agentImageName)
                                : undefined
                        }
                        conversationStarterOptions={vm.conversationStarterOptions}
                        onStarterClick={vm.onStarterClick}
                    />
                ) : (
                    <ChatHistory messages={vm.messages} />
                )}
            </div>
            <div className={vm.messagesEmpty ? `${styles.input} ${styles.inputEmptyState}` : styles.input}>
                <div className={vm.messagesEmpty ? styles.inputEmptyStateInner : undefined}>
                    {isAssistants && vm.sessionId ? (
                        <MigrateButton sessionId={vm.sessionId} />
                    ) : (
                        <ChatInput
                            prefillText={vm.prefillText}
                            prefillVersion={vm.prefillVersion}
                            layoutVariant={vm.messagesEmpty ? 'empty-state' : 'default'}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChatRoom;
